import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WORK } from "@/data/content";

function Card({ item, index }) {
  const hasLink = Boolean(item.url);
  const inner = (
    <>
      {/* hover glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#10b981]/0 blur-3xl transition-colors duration-500 group-hover:bg-[#10b981]/20" />

      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.3em] text-[#7c7a72]">
            {String(index + 1).padStart(2, "0")}
          </span>
          {hasLink ? (
            <span
              data-testid={`work-live-${index}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#10b981]/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#10b981]"
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10b981]" />
              Live demo
            </span>
          ) : (
            <ArrowUpRight className="h-6 w-6 text-[#7c7a72] transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#10b981]" />
          )}
        </div>

        <h3 className="mt-8 font-display text-5xl md:text-6xl leading-none text-[#f2f0e6]">
          {item.title}
        </h3>
        <p className="mt-5 max-w-md text-base leading-relaxed text-[#b9b6a8]">
          {item.pitch}
        </p>
      </div>

      <div className="relative mt-10 flex flex-wrap items-center gap-2">
        {item.stack.map((t) => (
          <span
            key={t}
            className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-[#b9b6a8]"
          >
            {t}
          </span>
        ))}
        {hasLink && (
          <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#10b981]">
            Visit <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        )}
      </div>
    </>
  );

  const className =
    "group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10 transition-[border-color,background-color] duration-400 hover:border-[#10b981]/60";

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      data-testid={`work-card-${index}`}
    >
      {hasLink ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className={className}>
          {inner}
        </a>
      ) : (
        <article className={className}>{inner}</article>
      )}
    </motion.div>
  );
}

export default function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative z-30 bg-[#050505] px-6 py-28 md:px-10 md:py-40"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-5 flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-[#7c7a72]">
              <span className="h-px w-10 bg-[#10b981]" />
              Selected Work
            </div>
            <h2 className="font-display text-6xl md:text-8xl leading-[0.85] text-[#f2f0e6]">
              Things I've
              <br />
              <span className="text-[#10b981]">built.</span>
            </h2>
          </div>
          <p className="max-w-sm text-base text-[#b9b6a8]">
            Real tools solving real problems for real businesses — pharmacies,
            distributors, and small retail.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {WORK.map((w, i) => (
            <Card key={w.title} item={w} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
